import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, eur } from "@/lib/apiClient";
import { Brand } from "@/components/Brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AdesioneNotice } from "@/components/ComplianceNotice";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ShieldCheck, Loader2, CheckCircle2, UploadCloud, FileCheck2, Info, Lock,
} from "lucide-react";

export default function AdesioneDistanza() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cliente, setCliente] = useState({ codice_fiscale: "", email: "", cellulare: "" });
  const [consents, setConsents] = useState({});
  const [uploaded, setUploaded] = useState([]);
  const [uploading, setUploading] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpDest, setOtpDest] = useState("");
  const [sendingOtp, setSendingOtp] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setInterval(() => setResendIn((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/public/adesione/${token}`);
      setData(d);
      setCliente({ codice_fiscale: d.prefill.codice_fiscale, email: d.prefill.email, cellulare: d.prefill.cellulare });
      setUploaded(d.documenti_caricati || []);
      if (d.status === "completata") setDone(true);
    } catch (e) {
      setData({ error: apiError(e) });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const uploadDoc = async (tipo, file) => {
    if (!file) return;
    setUploading(tipo);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      fd.append("file", file);
      const { data: r } = await api.post(`/public/adesione/${token}/documento`, fd);
      setUploaded(r.caricati);
      toast.success("Documento caricato");
    } catch (e) { toast.error(apiError(e)); }
    finally { setUploading(""); }
  };

  const sendOtp = async () => {
    setSendingOtp(true);
    try {
      const { data: r } = await api.post(`/public/adesione/${token}/otp/invia`, { cliente });
      setOtpSent(true);
      setOtpDest(r.destinazione || "");
      setResendIn(r.cooldown || 60);
      toast.success(`Codice inviato a ${r.destinazione || "il tuo recapito"}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSendingOtp(false); }
  };

  const confirmAdesione = async () => {
    setSubmitting(true);
    try {
      const { data: r } = await api.post(`/public/adesione/${token}/submit`, { consents, cliente, otp_code: otpCode });
      toast.success(r.message || "Adesione completata");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiError(e)); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-cream"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  }
  if (data?.error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-cream px-4 text-center" data-testid="adesione-error">
        <Lock className="h-10 w-10 text-terracotta" />
        <h1 className="font-heading text-2xl font-semibold text-forest">Link non valido</h1>
        <p className="max-w-md text-muted-foreground">{data.error}</p>
      </div>
    );
  }

  const p = data.proposta;
  const allConsents = data.consensi.every((c) => !c.required || consents[c.key]);
  const allDocs = data.documenti_richiesti.every((d) => uploaded.includes(d.tipo));
  const allFields = cliente.codice_fiscale && cliente.email && cliente.cellulare;
  const canSubmit = allConsents && allDocs && allFields && !submitting;

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 md:px-6">
          <Brand />
          <span className="flex items-center gap-1.5 text-xs font-medium text-forest/70"><Lock className="h-3.5 w-3.5" /> Adesione sicura</span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 md:px-6" data-testid="adesione-page">
        {done ? (
          <Card className="flex flex-col items-center gap-3 rounded-2xl p-10 text-center" data-testid="adesione-success">
            <CheckCircle2 className="h-14 w-14 text-emerald-600" />
            <h1 className="font-heading text-2xl font-semibold text-forest">Adesione confermata</h1>
            <div className="max-w-md space-y-3 text-muted-foreground">
              <p>
                Gentile Cliente, abbiamo ricevuto correttamente la tua adesione e i tuoi documenti.
                L'Agenzia verificherà i dati e ti farà pervenire <b>gli estremi per il bonifico bancario</b> al
                fine di perfezionare l'acquisto del Prodotto <b className="text-forest">{p.prodotto_nome}{p.pacchetto ? ` ${p.pacchetto}` : ""}</b>
                {p.premio != null ? <> — Premio annuo lordo <b className="text-terracotta">{eur(p.premio)}</b></> : null}.
              </p>
              {data.recesso_giorni ? (
                <p>Ricordi che hai <b>{data.recesso_giorni} giorni</b> di tempo per esercitare il diritto di recesso dalla conclusione del contratto.</p>
              ) : null}
            </div>
            <p className="mt-1 text-xs font-medium text-forest/60">Assietica — Portale Partner</p>
          </Card>
        ) : (
          <>
            <div className="mb-6">
              <h1 className="font-heading text-3xl font-semibold text-forest">Adesione alla proposta</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Completa l'adesione a distanza in pochi passaggi: verifica la proposta, carica il documento
                d'identità e presta i consensi previsti dalla normativa.
              </p>
            </div>

            {/* PROPOSTA */}
            <Card className="mb-5 rounded-2xl border-forest/15 p-5" data-testid="adesione-proposta">
              <div className="mb-3 flex items-center gap-2 text-forest"><ShieldCheck className="h-5 w-5" /><h2 className="font-heading text-lg font-semibold">La tua proposta</h2></div>
              <dl className="grid gap-2 text-sm sm:grid-cols-2">
                {p.contraente && <div><dt className="text-muted-foreground">Contraente</dt><dd className="font-medium text-forest">{p.contraente}</dd></div>}
                <div><dt className="text-muted-foreground">Prodotto</dt><dd className="font-medium text-forest">{p.prodotto_nome}</dd></div>
                {p.pacchetto && <div><dt className="text-muted-foreground">Soluzione</dt><dd className="font-medium text-forest">{p.pacchetto}</dd></div>}
                {p.nucleo && <div><dt className="text-muted-foreground">Composizione</dt><dd className="font-medium text-forest">{p.nucleo}</dd></div>}
                <div><dt className="text-muted-foreground">Premio annuo lordo</dt><dd className="font-semibold text-terracotta">{eur(p.premio)}</dd></div>
              </dl>
              {p.garanzie?.length > 0 && (
                <div className="mt-3 rounded-lg bg-sage/40 p-3">
                  <table className="w-full text-sm">
                    <tbody>
                      {p.garanzie.map((g, i) => (
                        <tr key={i} className="border-b border-border/40 last:border-0">
                          <td className="py-1.5 text-forest/80">{g.label}</td>
                          <td className="py-1.5 text-right font-medium text-forest">{eur(g.capitale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* DATI CLIENTE */}
            <Card className="mb-5 rounded-2xl p-5" data-testid="adesione-dati">
              <h2 className="mb-3 font-heading text-lg font-semibold text-forest">I tuoi dati</h2>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5 sm:col-span-1">
                  <Label>Codice fiscale</Label>
                  <Input data-testid="adesione-cf" value={cliente.codice_fiscale} onChange={(e) => setCliente({ ...cliente, codice_fiscale: e.target.value.toUpperCase() })} placeholder="RSSMRA80A01H501U" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email personale</Label>
                  <Input data-testid="adesione-email" type="email" value={cliente.email} onChange={(e) => setCliente({ ...cliente, email: e.target.value })} placeholder="nome@email.it" />
                </div>
                <div className="space-y-1.5">
                  <Label>Cellulare</Label>
                  <Input data-testid="adesione-cell" value={cliente.cellulare} onChange={(e) => setCliente({ ...cliente, cellulare: e.target.value })} placeholder="+39 333 1234567" />
                </div>
              </div>
            </Card>

            {/* DOCUMENTI */}
            <Card className="mb-5 rounded-2xl p-5" data-testid="adesione-documenti">
              <h2 className="mb-1 font-heading text-lg font-semibold text-forest">Documenti</h2>
              <p className="mb-3 text-xs text-muted-foreground">Carica fronte e retro (JPG, PNG o PDF, max 12 MB). I file sono conservati in modo sicuro e visibili solo all'Agenzia.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {data.documenti_richiesti.map((d) => {
                  const ok = uploaded.includes(d.tipo);
                  return (
                    <label key={d.tipo} data-testid={`adesione-upload-${d.tipo}`}
                      className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed p-5 text-center transition-colors ${ok ? "border-emerald-500 bg-emerald-50" : "border-border hover:border-forest/40 hover:bg-sage/30"}`}>
                      {uploading === d.tipo ? <Loader2 className="h-6 w-6 animate-spin text-forest" />
                        : ok ? <FileCheck2 className="h-6 w-6 text-emerald-600" />
                        : <UploadCloud className="h-6 w-6 text-forest/60" />}
                      <span className="text-sm font-medium text-forest">{d.label}</span>
                      <span className="text-xs text-muted-foreground">{ok ? "Caricato ✓ — clicca per sostituire" : "Clicca per caricare"}</span>
                      <input type="file" accept="image/*,application/pdf" className="hidden"
                        onChange={(e) => uploadDoc(d.tipo, e.target.files?.[0])} />
                    </label>
                  );
                })}
              </div>
            </Card>

            {/* CONSENSI */}
            <Card className="mb-5 rounded-2xl p-5" data-testid="adesione-consensi">
              <h2 className="mb-3 font-heading text-lg font-semibold text-forest">Informative e consensi</h2>
              <div className="space-y-2">
                {data.consensi.map((c) => (
                  <div key={c.key} className="rounded-lg border border-border/60 p-3">
                    <label className="flex items-start gap-3">
                      <Checkbox data-testid={`adesione-consenso-${c.key}`} checked={!!consents[c.key]}
                        onCheckedChange={(v) => setConsents({ ...consents, [c.key]: !!v })} className="mt-0.5" />
                      <span className="text-sm leading-snug text-forest/90">{c.label}</span>
                    </label>
                    {c.testo && (
                      <Accordion type="single" collapsible className="mt-1">
                        <AccordionItem value="t" className="border-0">
                          <AccordionTrigger className="py-1 text-xs text-forest/60 hover:no-underline"><span className="flex items-center gap-1"><Info className="h-3 w-3" /> Leggi l'informativa</span></AccordionTrigger>
                          <AccordionContent className="text-xs leading-relaxed text-muted-foreground">{c.testo}</AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-snug text-muted-foreground">
                <Info className="mt-0.5 h-3 w-3 shrink-0" /> {data.disclaimer_bozza}
              </p>
            </Card>

            <AdesioneNotice className="mb-5" />

            {!otpSent ? (
              <>
                <Button size="lg" onClick={sendOtp} disabled={!canSubmit || sendingOtp} data-testid="adesione-invia-otp"
                  className="w-full gap-2 bg-forest text-white hover:bg-forest/90">
                  {sendingOtp ? <Loader2 className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
                  Firma e aderisci (ricevi codice via SMS)
                </Button>
                {!canSubmit && !sendingOtp && (
                  <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="adesione-hint">
                    Per procedere: compila i dati, carica i documenti e presta tutti i consensi.
                  </p>
                )}
              </>
            ) : (
              <Card className="rounded-2xl border-forest/20 p-5" data-testid="adesione-otp-card">
                <h2 className="mb-1 font-heading text-lg font-semibold text-forest">Conferma con il codice</h2>
                <p className="mb-3 text-sm text-muted-foreground">
                  Abbiamo inviato un codice a <b className="text-forest">{otpDest}</b>. Inseriscilo qui sotto per firmare e completare l'adesione.
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <Input data-testid="adesione-otp-input" value={otpCode} inputMode="numeric" maxLength={6}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="Codice a 6 cifre" className="text-center text-lg tracking-[0.4em] sm:max-w-[200px]" />
                  <Button size="lg" onClick={confirmAdesione} disabled={otpCode.length !== 6 || submitting}
                    data-testid="adesione-conferma-otp" className="flex-1 gap-2 bg-forest text-white hover:bg-forest/90">
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}
                    Conferma e aderisci
                  </Button>
                </div>
                <button type="button" onClick={sendOtp} disabled={resendIn > 0 || sendingOtp}
                  data-testid="adesione-reinvia-otp"
                  className="mt-3 text-xs text-forest/70 underline-offset-4 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60">
                  {resendIn > 0 ? `Reinvia codice tra ${resendIn}s` : "Non hai ricevuto il codice? Reinvia"}
                </button>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
